import Box from "@material-ui/core/Box";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles } from "@material-ui/core/styles";
import Switch from "@material-ui/core/Switch";
import clsx from "clsx";
import { Form, FormikProps } from "formik";
import React, { useContext, useState } from "react";
import ListItemText from "@material-ui/core/ListItemText/ListItemText";
import ProductReferenceResponse from "../../../../../api/models/ProductReferenceResponse";
import TestRequestResponse from "../../../../../api/models/test-request/TestRequestResponse";
import UserReferenceResponse from "../../../../../api/models/UserReferenceResponse";
import BackgroundInformationApi from "../../../../../api/services/BackgroundInformationApi";
import currencies from "../../../../../assets/data/currencies";
import CustomProgress from "../../../../../components/Alert/CustomProgress";
import { AutocompleteSelectable } from "../../../../../components/Autocomplete/AutocompleteSelectable.model";
import {
  CountrySearchAutocomplete,
  CustomerSearchAutocomplete,
  OemSearchAutocomplete,
} from "../../../../../components/Autocomplete/CustomSearchAutoComplete.instances";
import CustomButton from "../../../../../components/Button/CustomButton";
import { ConfirmationDialogParams } from "../../../../../components/Dialog/ConfirmationDialog/ConfirmationDialog";
import CustomDynamicDropdown from "../../../../../components/Dropdown/CustomDynamicDropdown";
import InfoBox from "../../../../../components/InfoBox";
import CustomDatePicker from "../../../../../components/Input/CustomDatePicker";
import CustomDropdown from "../../../../../components/Input/CustomDropdown";
import CustomTextField from "../../../../../components/Input/CustomTextField";
import { MASK_NUMBER, MASK_THOUSANDS_SEPARATOR } from "../../../../../components/Input/MaskConfigurations";
import ErrorSnackbarContext from "../../../../../contexts/ErrorSnackbarContext";
import { useLaboratories } from "../../../../../contexts/LaboratoriesContext";
import removeElementAt from "../../../../../functions/removeElementAt.function";
import useEnv from "../../../../../hooks/env";
import { tesaDarkRedError } from "../../../../../styles/colors";
import * as InfoMessages from "../../../messages";
import ApproversInput from "../../inputs/ApproversInput";
import DeadlineInput from "../../inputs/DeadlineInput";
import EstimatedFinishingDateInput from "../../inputs/EstimatedFinishingDateInput";
import InternalContactInput from "../../inputs/InternalContactInput";
import ProductsInput from "../../inputs/ProductsInput";
import SuggestedApproverInput from "../../inputs/SuggestedApproverInput";
import TestSpecificationInput from "../../inputs/TestSpecificationInput";
import DescriptionFormType from "../../models/Description.form";
import ProductEditData from "../../ProductEditData.model";
import TestRequestGallery from "../../TestRequestGallery";
import useContextFields from "../../useContextFields.hook";
import * as Labels from "../fieldLabels";
import * as Fields from "../fieldNames";
import { StyledMenu, StyledMenuItem } from "../../../../../components/Menu/StyledMenu";
import { getCompetitorProductReferences, getPrototypeProductReferences } from "../productReferenceHelper";
import {
  ApplicationFieldResponse,
  BusinessUnitResponse,
  MarketSegmentResponse,
} from "../../../../../api/services/ContextApi";
import CountryResponse from "../../../../../api/models/country/Country.response";
import CustomerResponse from "../../../../../api/models/customer/Customer.response";
import ProcessTypeInput from "../../inputs/ProcessTypeInput";

const useStyles = makeStyles({
  productsContainer: {
    "& > * + *": {
      marginTop: "18px",
    },
  },
  testInfoGridContainer: {
    "& .LEAPGrid-item-pb-4": {
      paddingBottom: "4px",
    },
    "& .LEAPGrid-item-pb-0": {
      paddingBottom: 0,
    },
  },
  testProgramSpecificationsContainer: {
    "& > * + *": {
      marginTop: (props: Partial<DescriptionFormInternalProps>) =>
        props.permissions?.editDescription ? "20px" : "32px",
    },
  },
});

type ConfirmationOverride = {
  [K in keyof DescriptionFormType]?: boolean;
};
type TestRequestDescriptionAttachment = Required<TestRequestRaw.Schemas.TestRequestAttachmentReferenceDTO>;
type TestRequestDescriptionPermissions = Pick<
  TestRequestRaw.Schemas.TestRequestPermissions,
  "description"
>["description"];
type User = TestRequestRaw.Schemas.UserInformation;

interface DescriptionFormInternalProps {
  attachedFilesReferences: TestRequestDescriptionAttachment[];
  formikProps: FormikProps<DescriptionFormType>;
  permissions: TestRequestDescriptionPermissions;
  submissionOrApprovalDate: Date | null;
  testRequestHistorical: boolean;
  testRequestId: string;
  testRequestObj: any;
  user: User | null;
  handleDirtyChange: (reset?: boolean) => void;
  handleSave: (
    values: DescriptionFormType,
    refresh?: boolean | undefined,
    confirmationOverride?: ConfirmationOverride,
  ) => Promise<TestRequestResponse | void>;
  showConfirmationDialog: (params?: ConfirmationDialogParams | undefined) => Promise<string>;
  showSalesProductsDialog: (productEditData?: ProductEditData<ProductReferenceResponse>) => void;
  showAddPrototypeDialog: (productEditData?: ProductEditData<ProductReferenceResponse>) => void;
  showCompetitorDialog: (productEditData?: ProductEditData<ProductReferenceResponse>) => void;
  scrollToError: () => void;
}

const DescriptionFormInternal: React.FC<DescriptionFormInternalProps> = ({
  attachedFilesReferences,
  formikProps,
  handleDirtyChange,
  handleSave,
  permissions,
  scrollToError,
  showConfirmationDialog,
  showSalesProductsDialog,
  showAddPrototypeDialog,
  showCompetitorDialog,
  submissionOrApprovalDate,
  testRequestHistorical,
  testRequestId,
  testRequestObj,
  user,
}) => {
  const classes = useStyles({ permissions });
  const { errors, handleBlur, handleChange, initialValues, setFieldValue, touched, values } = formikProps;
  const { api } = useEnv();
  const aproe = testRequestObj?.purpose === "APPLICATION_PROCESS";
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const [showSpecificationInput, setShowSpecificationInput] = useState(values.specifications.length > 0);
  const backgroundInformationApi: BackgroundInformationApi = new BackgroundInformationApi(api);
  const createMode = testRequestId === "new";
  const newOrDraft = createMode || testRequestObj?.state === "DRAFT";
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const testRequestPrePreparationStatus = ["DRAFT", "SUBMITTED", "FOR_EVALUATION", "FOR_REVIEW", "FOR_APPROVAL"];
  const isStatusPreparationAndFollowing =
    testRequestObj?.status?.state && testRequestPrePreparationStatus.indexOf(testRequestObj.status.state) === -1;
  const hasHighlyConfidential =
    values.projectConfidentiality === "HIGHLY_CONFIDENTIAL" ||
    testRequestObj?.description?.projectConfidentiality === "HIGHLY_CONFIDENTIAL";
  // In DRAFT/new requests this field must remain editable, even if HIGHLY_CONFIDENTIAL is selected.
  const isConfidentialityReadOnly = !newOrDraft && hasHighlyConfidential;
  const showHighlyConfidentialOption = hasHighlyConfidential || !isStatusPreparationAndFollowing;

  const searchLocations = (query: string) => {
    return backgroundInformationApi.searchLocations(query).then((res) => res?.data);
  };

  const searchOems = (query: string) => {
    return backgroundInformationApi.searchOems(query).then((res) => res?.data);
  };

  const handleLocalChange = (e: React.ChangeEvent<unknown>) => {
    if (!e) return;
    handleDirtyChange();
    handleChange(e);
  };

  const handleLocalBlur = (e: React.FocusEvent<unknown>) => {
    if (!e) return;
    handleBlur(e);
  };

  const handleApproverChange = (approver: UserReferenceResponse | null) => {
    if (approver?.userId !== values[Fields.APPROVER]?.userId) {
      setFieldValue(Fields.APPROVER, approver);
      handleDirtyChange();
    }
  };

  const handleDeleteProduct = ({ data }: AutocompleteSelectable<any>, index: number) => {
    if (data?.type) {
      if (data.type === Fields.PROTOTYPE_PRODUCT_REFERENCES) {
        const prototypeProductToDelete =
          values[Fields.PROTOTYPE_PRODUCT_REFERENCES] &&
          values[Fields.PROTOTYPE_PRODUCT_REFERENCES].filter((product) => {
            return (
              product.prototypeProductId !== data.prototypeProductId ||
              product.prototypeProductName !== data.prototypeProductName
            );
          });
        setFieldValue(Fields.PROTOTYPE_PRODUCT_REFERENCES, prototypeProductToDelete);
      }
      if (data.type === Fields.COMPETITOR_PRODUCT_REFERENCES) {
        const competitorProductToDelete =
          values[Fields.COMPETITOR_PRODUCT_REFERENCES] &&
          values[Fields.COMPETITOR_PRODUCT_REFERENCES].filter((product) => {
            return (
              product.competitorProductId !== data.competitorProductId ||
              product.competitorProductName !== data.competitorProductName
            );
          });
        setFieldValue(Fields.COMPETITOR_PRODUCT_REFERENCES, competitorProductToDelete);
      }
    } else {
      const productToDelete = removeElementAt(index, values[Fields.PRODUCT_REFERENCES]);
      setFieldValue(Fields.PRODUCT_REFERENCES, productToDelete);
    }

    handleDirtyChange();
  };

  const handleResetProducts = () => {
    setFieldValue(Fields.PRODUCT_REFERENCES, []);
    setFieldValue(Fields.PROTOTYPE_PRODUCT_REFERENCES, []);
    setFieldValue(Fields.COMPETITOR_PRODUCT_REFERENCES, []);
    handleDirtyChange();
  };

  const handleSpecificationToggleChange = () => {
    if (permissions?.editDescription && showSpecificationInput && values.specifications.length > 0) {
      return showConfirmationDialog({
        confirmColor: tesaDarkRedError,
        confirmText: "Remove Specifications",
        text: "Do you really want to remove all Customer Specifications or Standards?",
      })
        .then(() => {
          setShowSpecificationInput(!showSpecificationInput);
          setFieldValue("specifications", []);
          handleDirtyChange();
        })
        .catch(() => {
          // do nothing on dialog close;
        });
    }
    setShowSpecificationInput(!showSpecificationInput);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const {
    applicationField,
    applicationFieldLoadKey,
    businessUnit,
    loadApplicationFields,
    loadBusinessUnits,
    loadMarketSegments,
    marketSegment,
    marketSegmentLoadKey,
    preparing,
    setApplicationField,
    setBusinessUnit,
    setMarketSegment,
  } = useContextFields(initialValues);

  const { labs, labsLoading } = useLaboratories(testRequestObj?.purpose || "DEFAULT");

  if (preparing) {
    return <CustomProgress />;
  }

  const products = [
    ...(values[Fields.PRODUCT_REFERENCES] || []),
    ...(getCompetitorProductReferences(values[Fields.COMPETITOR_PRODUCT_REFERENCES]) || []),
    ...(getPrototypeProductReferences(values[Fields.PROTOTYPE_PRODUCT_REFERENCES]) || []),
  ];

  return (
    <Form>
      <>
        <h3 style={{ marginTop: "8px" }}>Test Information</h3>
        <Grid
          classes={{ root: classes.testInfoGridContainer }}
          container
          justify="space-between"
          direction="row"
          spacing={4}
        >
          <Grid item lg={6} md={6} sm={12} xs={12}>
            {aproe ? (
              <CustomTextField
                fullWidth
                disabled={!permissions?.editDescription}
                error={!!(errors[Fields.CUSTOMER_PART] && touched[Fields.CUSTOMER_PART])}
                helperText={
                  errors[Fields.CUSTOMER_PART] && touched[Fields.CUSTOMER_PART]
                    ? errors[Fields.CUSTOMER_PART]
                    : "Separate multiple entries with a comma"
                }
                id={Fields.CUSTOMER_PART}
                label={Labels.CUSTOMER_PART}
                name={Fields.CUSTOMER_PART}
                onBlur={handleBlur}
                onChange={handleLocalChange}
                value={values[Fields.CUSTOMER_PART]}
              />
            ) : (
              <CustomTextField
                fullWidth
                disabled={!permissions?.editDescription}
                error={!!(errors[Fields.SUBSTRATES] && touched[Fields.SUBSTRATES])}
                helperText={
                  errors[Fields.SUBSTRATES] && touched[Fields.SUBSTRATES]
                    ? errors[Fields.SUBSTRATES]
                    : "Separate multiple entries with a comma"
                }
                id={Fields.SUBSTRATES}
                label={Labels.SUBSTRATES}
                name={Fields.SUBSTRATES}
                onBlur={handleBlur}
                onChange={handleLocalChange}
                value={values[Fields.SUBSTRATES]}
              />
            )}
          </Grid>
          <Grid className={clsx("LEAPGrid-item-pb-0", classes.productsContainer)} item lg={6} md={6} sm={12} xs={12}>
            <ProductsInput
              mandatory
              helperText={
                errors[Fields.PRODUCT_REFERENCES] &&
                errors[Fields.COMPETITOR_PRODUCT_REFERENCES] &&
                errors[Fields.PROTOTYPE_PRODUCT_REFERENCES] &&
                touched[Fields.PRODUCT_REFERENCES]
                  ? "Select a product"
                  : ""
              }
              error={
                !!(
                  errors[Fields.PRODUCT_REFERENCES] &&
                  errors[Fields.COMPETITOR_PRODUCT_REFERENCES] &&
                  errors[Fields.PROTOTYPE_PRODUCT_REFERENCES] &&
                  touched[Fields.PRODUCT_REFERENCES]
                )
              }
              disabled={!permissions?.editDescription}
              label={Labels.PRODUCT_REFERENCES}
              onDelete={handleDeleteProduct}
              onReset={handleResetProducts}
              onTagClick={(e) => {
                if (e?.data?.type) {
                  if (e.data?.type === Fields.PROTOTYPE_PRODUCT_REFERENCES) {
                    return showAddPrototypeDialog(e);
                  }
                  if (e.data?.type === Fields.COMPETITOR_PRODUCT_REFERENCES) {
                    return showCompetitorDialog(e);
                  }
                }
                return showSalesProductsDialog(e);
              }}
              values={products}
            />
            <CustomButton
              add
              aria-controls="customized-menu"
              aria-haspopup="true"
              disabled={!permissions?.editDescription}
              onClick={handleClick}
              text="Add Product"
            />
            <StyledMenu
              id="customized-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <StyledMenuItem
                onClick={() => {
                  handleClose();
                  showSalesProductsDialog();
                }}
              >
                <ListItemText primary="tesa Product" />
              </StyledMenuItem>
              <StyledMenuItem
                onClick={() => {
                  handleClose();
                  showAddPrototypeDialog();
                }}
              >
                <ListItemText primary="tesa Prototype" />
              </StyledMenuItem>
              <StyledMenuItem
                onClick={() => {
                  handleClose();
                  showCompetitorDialog();
                }}
              >
                <ListItemText primary="Competitor Product" />
              </StyledMenuItem>
            </StyledMenu>
          </Grid>
          <Grid
            item
            lg={6}
            md={6}
            sm={12}
            xs={12}
            className={clsx({
              "LEAPGrid-item-pb-4": !showSpecificationInput,
            })}
          >
            <Grid container className={classes.testProgramSpecificationsContainer}>
              <Grid item xs={12}>
                <InfoBox alignTop text={InfoMessages.TEST_PROGRAM}>
                  {aproe ? (
                    <CustomTextField
                      fullWidth
                      multiline
                      disabled={!permissions?.editDescription}
                      error={!!(errors[Fields.PROCESS_DETAIL] && touched[Fields.PROCESS_DETAIL])}
                      helperText={
                        errors[Fields.PROCESS_DETAIL] && touched[Fields.PROCESS_DETAIL]
                          ? errors[Fields.PROCESS_DETAIL]
                          : ""
                      }
                      icon={!!(errors[Fields.PROCESS_DETAIL] && touched[Fields.PROCESS_DETAIL])}
                      id={Fields.PROCESS_DETAIL}
                      label={Labels.PROCESS_DETAIL}
                      name={Fields.PROCESS_DETAIL}
                      onBlur={handleBlur}
                      onChange={handleLocalChange}
                      rows={5}
                      value={values[Fields.PROCESS_DETAIL]}
                    />
                  ) : (
                    <CustomTextField
                      fullWidth
                      multiline
                      disabled={!permissions?.editDescription}
                      error={!!(errors[Fields.TEST_PROGRAM] && touched[Fields.TEST_PROGRAM])}
                      helperText={
                        errors[Fields.TEST_PROGRAM] && touched[Fields.TEST_PROGRAM] ? errors[Fields.TEST_PROGRAM] : ""
                      }
                      icon={!!(errors[Fields.TEST_PROGRAM] && touched[Fields.TEST_PROGRAM])}
                      id={Fields.TEST_PROGRAM}
                      label={Labels.TEST_PROGRAM}
                      name={Fields.TEST_PROGRAM}
                      onBlur={handleBlur}
                      onChange={handleLocalChange}
                      rows={5}
                      value={values[Fields.TEST_PROGRAM]}
                    />
                  )}
                </InfoBox>
              </Grid>
              {!aproe && (
                <Grid item xs={12}>
                  {permissions?.editDescription && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showSpecificationInput}
                          color="secondary"
                          onChange={handleSpecificationToggleChange}
                          name="specificationInputToggle"
                        />
                      }
                      label="I know the exact specification"
                    />
                  )}
                  {showSpecificationInput && (
                    <TestSpecificationInput
                      {...formikProps}
                      disabled={!permissions?.editDescription}
                      handleDirtyChange={handleDirtyChange}
                    />
                  )}
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <CustomTextField
              fullWidth
              multiline
              disabled={!permissions?.editDescription}
              error={!!(errors[Fields.BACKGROUND_INFORMATION] && touched[Fields.BACKGROUND_INFORMATION])}
              helperText={
                errors[Fields.BACKGROUND_INFORMATION] && touched[Fields.BACKGROUND_INFORMATION]
                  ? errors[Fields.BACKGROUND_INFORMATION]
                  : "Detailed description of the application & purpose of the test request"
              }
              icon={!!(errors[Fields.BACKGROUND_INFORMATION] && touched[Fields.BACKGROUND_INFORMATION])}
              id={Fields.BACKGROUND_INFORMATION}
              label={Labels.BACKGROUND_INFORMATION}
              name={Fields.BACKGROUND_INFORMATION}
              onBlur={handleBlur}
              onChange={handleLocalChange}
              rows={5}
              value={values[Fields.BACKGROUND_INFORMATION]}
            />
          </Grid>
          <Grid item lg={3} md={3} sm={6} xs={6}>
            <InfoBox text={InfoMessages.infoCscLaboratory(permissions?.editDescription, newOrDraft)}>
              <CustomDropdown
                mandatory
                disabled={!permissions?.editDescription || !newOrDraft}
                error={!!(errors[Fields.CSC_LAB] && touched[Fields.CSC_LAB])}
                helperText={errors[Fields.CSC_LAB] && touched[Fields.CSC_LAB] ? errors[Fields.CSC_LAB] : ""}
                labelText={Labels.CSC_LAB}
                name={Fields.CSC_LAB}
                onBlur={handleBlur}
                onChange={handleLocalChange}
                value={values[Fields.CSC_LAB]}
              >
                <MenuItem value="">
                  <em>{labsLoading ? "Loading..." : "Please Choose"}</em>
                </MenuItem>
                {!labsLoading &&
                  labs.map((lab) => (
                    <MenuItem key={lab.key} value={lab.key}>
                      {lab.name}
                    </MenuItem>
                  ))}
              </CustomDropdown>
            </InfoBox>
          </Grid>
          {aproe && (
            <Grid item lg={3} md={3} sm={6} xs={6}>
              <ProcessTypeInput
                disabled={!permissions?.editDescription}
                error={!!(errors[Fields.PROCESS_TYPE] && touched[Fields.PROCESS_TYPE])}
                helperText={
                  errors[Fields.PROCESS_TYPE] && touched[Fields.PROCESS_TYPE] ? errors[Fields.PROCESS_TYPE] : ""
                }
                labelText={Labels.PROCESS_TYPE}
                name={Fields.PROCESS_TYPE}
                onChange={handleLocalChange}
                value={values[Fields.PROCESS_TYPE]}
              />
            </Grid>
          )}
          <Grid item lg={3} md={3} sm={6} xs={6}>
            <CustomDropdown
              disabled={!permissions?.editDescription || isConfidentialityReadOnly}
              error={!!(errors[Fields.CONFIDENTIALITY] && touched[Fields.CONFIDENTIALITY])}
              helperText={
                errors[Fields.CONFIDENTIALITY] && touched[Fields.CONFIDENTIALITY] ? errors[Fields.CONFIDENTIALITY] : ""
              }
              labelText={Labels.CONFIDENTIALITY}
              mandatory
              name={Fields.CONFIDENTIALITY}
              onChange={handleLocalChange}
              onBlur={handleLocalBlur}
              value={values[Fields.CONFIDENTIALITY]}
            >
              <MenuItem value="">
                <em>Please Choose</em>
              </MenuItem>
              <MenuItem value="INTERNAL">Internal (shared internally)</MenuItem>
              <MenuItem value="NORMAL_CONFIDENTIAL">Confidential (shared with the NDA stakeholder)</MenuItem>
              {showHighlyConfidentialOption && (
                <MenuItem value="HIGHLY_CONFIDENTIAL">Secret Project (Attention! Risk of high penalties)</MenuItem>
              )}
            </CustomDropdown>
          </Grid>
          {!aproe && (
            <Grid item lg={3} md={3} sm={6} xs={6}>
              <CustomDropdown
                disabled={!permissions?.editDescription}
                error={!!(errors[Fields.REPORT_LANGUAGE] && touched[Fields.REPORT_LANGUAGE])}
                helperText={
                  errors[Fields.REPORT_LANGUAGE] && touched[Fields.REPORT_LANGUAGE]
                    ? errors[Fields.REPORT_LANGUAGE]
                    : ""
                }
                labelText={Labels.REPORT_LANGUAGE}
                name={Fields.REPORT_LANGUAGE}
                onChange={handleLocalChange}
                value={values[Fields.REPORT_LANGUAGE]}
                mandatory
              >
                <MenuItem value="">
                  <em>Please Choose</em>
                </MenuItem>
                <MenuItem value="ENGLISH">English</MenuItem>
                <MenuItem value="GERMAN">German</MenuItem>
                <MenuItem value="CHINESE">Chinese</MenuItem>
                <MenuItem value="JAPANESE">Japanese</MenuItem>
                <MenuItem value="KOREAN">Korean</MenuItem>
                <MenuItem value="THAI">Thai</MenuItem>
                <MenuItem value="VIETNAMESE">Vietnamese</MenuItem>
              </CustomDropdown>
            </Grid>
          )}
          <Grid item lg={3} md={3} sm={6} xs={6}>
            {!testRequestHistorical && (
              <DeadlineInput
                deadline={testRequestObj.description?.deadline}
                disabled={!permissions?.editDescription}
                disablePast={!!permissions?.editDescription}
                handleDirtyChange={handleDirtyChange}
                isAproe={aproe}
                showConfirmationDialog={showConfirmationDialog}
                {...formikProps}
              />
            )}
          </Grid>
        </Grid>
        {!newOrDraft && !aproe && (
          <Box>
            <Box height={16} />
            <InfoBox compress text={InfoMessages.CSC_EVALUATION}>
              <h3>CSC Evaluation</h3>
            </InfoBox>
            <Grid container spacing={4}>
              <Grid item lg={3} md={3} sm={6} xs={6}>
                <InfoBox text={InfoMessages.REPORT_USAGE}>
                  <CustomDropdown
                    disabled={!permissions?.editDescription}
                    error={!!(errors[Fields.REPORT_USAGE] && touched[Fields.REPORT_USAGE])}
                    labelText={Labels.REPORT_USAGE}
                    name={Fields.REPORT_USAGE}
                    value={values[Fields.REPORT_USAGE]}
                    onChange={handleLocalChange}
                  >
                    <MenuItem value="">
                      <em>Please Choose</em>
                    </MenuItem>
                    <MenuItem value="ONLY_INTERNAL">Internal</MenuItem>
                    <MenuItem value="EXTERNAL">External</MenuItem>
                  </CustomDropdown>
                </InfoBox>
              </Grid>
              <Grid item lg={3} md={3} sm={6} xs={6}>
                <InfoBox text={InfoMessages.TEST_LOCATION}>
                  <CustomDropdown
                    disabled={!permissions?.editDescription}
                    error={!!(errors[Fields.TEST_LOCATION] && touched.testLocation)}
                    labelText={Labels.TEST_LOCATION}
                    name={Fields.TEST_LOCATION}
                    onChange={handleLocalChange}
                    value={values[Fields.TEST_LOCATION]}
                  >
                    <MenuItem value="">
                      <em>Please Choose</em>
                    </MenuItem>
                    <MenuItem value="CSC_LAB">CSC Lab</MenuItem>
                    <MenuItem value="THIRD_PARTY_LAB">3rd Party Lab</MenuItem>
                  </CustomDropdown>
                </InfoBox>
              </Grid>
              <Grid item lg={3} md={3} sm={6} xs={6}>
                <CustomTextField
                  mandatory
                  disabled={!permissions?.editAdditional}
                  fullWidth
                  error={!!(errors[Fields.TEST_EFFORT_IN_DAYS] && touched[Fields.TEST_EFFORT_IN_DAYS])}
                  helperText={
                    errors[Fields.TEST_EFFORT_IN_DAYS] && touched[Fields.TEST_EFFORT_IN_DAYS]
                      ? errors[Fields.TEST_EFFORT_IN_DAYS]
                      : "Specify estimated test effort in days"
                  }
                  id={Fields.TEST_EFFORT_IN_DAYS}
                  label={Labels.TEST_EFFORT_IN_DAYS}
                  masked={MASK_NUMBER}
                  name={Fields.TEST_EFFORT_IN_DAYS}
                  onBlur={handleBlur}
                  onChange={handleLocalChange}
                  value={values[Fields.TEST_EFFORT_IN_DAYS]}
                />
              </Grid>
              <Grid item lg={3} md={3} sm={6} xs={6}>
                {!testRequestHistorical && (
                  <EstimatedFinishingDateInput
                    estimatedFinishingDate={testRequestObj?.description?.estimatedFinishingDate || ""}
                    disabled={!permissions?.editAdditional}
                    disablePast={!!permissions?.editAdditional}
                    handleDirtyChange={handleDirtyChange}
                    showConfirmationDialog={showConfirmationDialog}
                    {...formikProps}
                  />
                )}
              </Grid>
            </Grid>
          </Box>
        )}
        <Box height={16} />
        <TestRequestGallery
          disabled={!permissions?.editAttachments}
          fileReferences={attachedFilesReferences}
          isInCreateMode={createMode}
          onSave={() => handleSave(values).catch(() => scrollToError())}
          testRequestId={testRequestId}
        />
        <Box>
          <Box height={16} />
          <h3>Project Information</h3>
          <Grid container spacing={4}>
            <Grid item lg={3} md={3} sm={6} xs={6}>
              <CustomDynamicDropdown<BusinessUnitResponse>
                disabled={!permissions?.editDescription}
                error={!!(errors[Fields.BUSINESS_UNIT] && touched[Fields.BUSINESS_UNIT])}
                helperText={
                  errors[Fields.BUSINESS_UNIT] && touched[Fields.BUSINESS_UNIT] ? errors[Fields.BUSINESS_UNIT] : ""
                }
                labelText={Labels.BUSINESS_UNIT}
                load={loadBusinessUnits}
                mandatory
                mapData={(data) => ({ key: `${data.id!}`, label: data.name!, data })}
                onChange={(value) => {
                  setBusinessUnit(value);
                  setMarketSegment(null);
                  setApplicationField(null);
                  setFieldValue(Fields.BUSINESS_UNIT, value?.name || "");
                  setFieldValue(Fields.MARKET_SEGMENT, "");
                  setFieldValue(Fields.APPLICATION_FIELD, "");
                  handleDirtyChange();
                }}
                onGeneralError={handleHttpError}
                value={businessUnit}
              />
            </Grid>
            <Grid item lg={3} md={3} sm={6} xs={6}>
              <CustomDynamicDropdown<MarketSegmentResponse>
                disabled={!permissions?.editDescription || !values[Fields.BUSINESS_UNIT]}
                error={!!(errors[Fields.MARKET_SEGMENT] && touched[Fields.MARKET_SEGMENT])}
                helperText={
                  values[Fields.BUSINESS_UNIT]
                    ? errors[Fields.MARKET_SEGMENT]
                    : "Select a business unit to enable dropdown"
                }
                labelText={Labels.MARKET_SEGMENT}
                load={loadMarketSegments}
                loadKey={marketSegmentLoadKey}
                mandatory
                mapData={(data) => ({ key: `${data.id!}`, label: data.name!, data })}
                onChange={(value): void => {
                  setMarketSegment(value);
                  setApplicationField(null);
                  setFieldValue(Fields.MARKET_SEGMENT, value?.name || "");
                  setFieldValue(Fields.APPLICATION_FIELD, "");
                  handleDirtyChange();
                }}
                onGeneralError={handleHttpError}
                value={marketSegment}
              />
            </Grid>
            <Grid item lg={3} md={3} sm={6} xs={6}>
              <CustomTextField
                disabled={!permissions?.editDescription}
                fullWidth
                error={!!(errors[Fields.CRM_NO] && touched[Fields.CRM_NO])}
                helperText={errors[Fields.CRM_NO] && touched[Fields.CRM_NO] ? errors[Fields.CRM_NO] : ""}
                icon={!!errors[Fields.CRM_NO]}
                id={Fields.CRM_NO}
                label={Labels.CRM_NO}
                name={Fields.CRM_NO}
                onBlur={handleBlur}
                onChange={handleLocalChange}
                value={values[Fields.CRM_NO]}
              />
            </Grid>
            <Grid item lg={3} md={3} sm={6} xs={6}>
              <InfoBox text={InfoMessages.BUSINESS_TYPE}>
                <CustomDropdown
                  disabled={!permissions?.editDescription}
                  error={!!(errors[Fields.BUSINESS_TYPE] && touched[Fields.BUSINESS_TYPE])}
                  id={Fields.BUSINESS_TYPE}
                  name={Fields.BUSINESS_TYPE}
                  onChange={handleLocalChange}
                  labelText={Labels.BUSINESS_TYPE}
                  value={values[Fields.BUSINESS_TYPE]}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="NEW">Incremental</MenuItem>
                  <MenuItem value="MAINTAIN">Replacement</MenuItem>
                  <MenuItem value="BASIC_ANALYSIS">BasicAnalysis</MenuItem>
                </CustomDropdown>
              </InfoBox>
            </Grid>
            <Grid item lg={6} md={6} sm={12} xs={12}>
              <CustomDynamicDropdown<ApplicationFieldResponse>
                disabled={
                  !permissions?.editDescription || !values[Fields.MARKET_SEGMENT] || !values[Fields.BUSINESS_UNIT]
                }
                error={!!(errors[Fields.APPLICATION_FIELD] && touched[Fields.APPLICATION_FIELD])}
                helperText={
                  values[Fields.BUSINESS_UNIT] && values[Fields.MARKET_SEGMENT]
                    ? errors[Fields.APPLICATION_FIELD]
                    : "Select a business unit & market segment to enable dropdown"
                }
                labelText={Labels.APPLICATION_FIELD}
                load={loadApplicationFields}
                loadKey={applicationFieldLoadKey}
                mandatory
                mapData={(data) => ({ key: `${data.id!}`, label: data.name!, data })}
                onChange={(value): void => {
                  setApplicationField(value);
                  setFieldValue(Fields.APPLICATION_FIELD, value?.name || "");
                  handleDirtyChange();
                }}
                onGeneralError={handleHttpError}
                value={applicationField}
              />
            </Grid>
            <Grid item lg={3} md={3} sm={6} xs={6}>
              <CustomTextField
                mandatory
                disabled={!permissions?.editDescription}
                fullWidth
                error={!!(errors[Fields.PNS] && touched[Fields.PNS])}
                helperText={
                  errors[Fields.PNS] && touched[Fields.PNS]
                    ? errors[Fields.PNS]
                    : "Net sales potential opp. value per year"
                }
                icon={!!(errors[Fields.PNS] && touched[Fields.PNS])}
                id={Fields.PNS}
                label={Labels.PNS}
                masked={MASK_THOUSANDS_SEPARATOR}
                name={Fields.PNS}
                onBlur={handleBlur}
                onChange={handleLocalChange}
                value={values[Fields.PNS] || ""}
              />
            </Grid>
            <Grid item lg={3} md={3} sm={6} xs={6}>
              <CustomDropdown
                mandatory
                disabled={!permissions?.editDescription}
                labelText={Labels.CURRENCY}
                name={Fields.CURRENCY}
                onChange={handleLocalChange}
                value={values[Fields.CURRENCY]}
                error={!!(errors[Fields.CURRENCY] && touched[Fields.CURRENCY])}
                helperText={errors[Fields.CURRENCY] && touched[Fields.CURRENCY] ? errors[Fields.CURRENCY] : ""}
              >
                {currencies.map((currency) => {
                  return (
                    <MenuItem value={currency} key={`${currency}-option`}>
                      {currency}
                    </MenuItem>
                  );
                })}
              </CustomDropdown>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <CustomTextField
                disabled={!permissions?.editDescription}
                error={!!(errors[Fields.SOLUTION] && touched[Fields.SOLUTION])}
                fullWidth
                helperText={
                  errors[Fields.SOLUTION] && touched[Fields.SOLUTION]
                    ? errors[Fields.SOLUTION]
                    : "Describe the tape or process currently applied by the customer"
                }
                icon={!!(errors[Fields.SOLUTION] && touched[Fields.SOLUTION])}
                id={Fields.SOLUTION}
                label={Labels.SOLUTION}
                multiline
                name={Fields.SOLUTION}
                onBlur={handleBlur}
                onChange={handleLocalChange}
                rows={5}
                value={values[Fields.SOLUTION]}
              />
            </Grid>
            {aproe && (
              <Grid item lg={3} md={3} sm={6} xs={6}>
                <CustomTextField
                  disabled={!permissions?.editDescription}
                  fullWidth
                  error={!!(errors[Fields.EXTERNAL_DEVELOPMENT_BUDGET] && touched[Fields.EXTERNAL_DEVELOPMENT_BUDGET])}
                  helperText={
                    errors[Fields.EXTERNAL_DEVELOPMENT_BUDGET] && touched[Fields.EXTERNAL_DEVELOPMENT_BUDGET]
                      ? errors[Fields.EXTERNAL_DEVELOPMENT_BUDGET]
                      : ""
                  }
                  icon={!!(errors[Fields.EXTERNAL_DEVELOPMENT_BUDGET] && touched[Fields.EXTERNAL_DEVELOPMENT_BUDGET])}
                  id={Fields.EXTERNAL_DEVELOPMENT_BUDGET}
                  label={Labels.EXTERNAL_DEVELOPMENT_BUDGET}
                  masked={MASK_THOUSANDS_SEPARATOR}
                  name={Fields.EXTERNAL_DEVELOPMENT_BUDGET}
                  onBlur={handleBlur}
                  onChange={handleLocalChange}
                  value={values[Fields.EXTERNAL_DEVELOPMENT_BUDGET] || ""}
                />
              </Grid>
            )}
            {aproe && (
              <Grid item lg={3} md={3} sm={6} xs={6}>
                <CustomDropdown
                  disabled={!permissions?.editDescription}
                  labelText={Labels.EXTERNAL_DEVELOPMENT_BUDGET_CURRENCY_CODE}
                  name={Fields.EXTERNAL_DEVELOPMENT_BUDGET_CURRENCY_CODE}
                  onChange={handleLocalChange}
                  value={values[Fields.EXTERNAL_DEVELOPMENT_BUDGET_CURRENCY_CODE]}
                  error={
                    !!(
                      errors[Fields.EXTERNAL_DEVELOPMENT_BUDGET_CURRENCY_CODE] &&
                      touched[Fields.EXTERNAL_DEVELOPMENT_BUDGET_CURRENCY_CODE]
                    )
                  }
                  helperText={
                    errors[Fields.EXTERNAL_DEVELOPMENT_BUDGET_CURRENCY_CODE] &&
                    touched[Fields.EXTERNAL_DEVELOPMENT_BUDGET_CURRENCY_CODE]
                      ? errors[Fields.EXTERNAL_DEVELOPMENT_BUDGET_CURRENCY_CODE]
                      : ""
                  }
                >
                  {currencies.map((currency) => {
                    return (
                      <MenuItem value={currency} key={`${currency}-option`}>
                        {currency}
                      </MenuItem>
                    );
                  })}
                </CustomDropdown>
              </Grid>
            )}
            <Grid item lg={3} md={3} sm={6} xs={6}>
              <CustomDropdown
                mandatory
                disabled={!permissions?.editDescription}
                error={!!(errors[Fields.TEST_PURPOSE] && touched[Fields.TEST_PURPOSE])}
                id={Fields.TEST_PURPOSE}
                name={Fields.TEST_PURPOSE}
                onChange={handleLocalChange}
                labelText={Labels.TEST_PURPOSE}
                value={values[Fields.TEST_PURPOSE]}
                helperText={
                  errors[Fields.TEST_PURPOSE] && touched[Fields.TEST_PURPOSE] ? errors[Fields.TEST_PURPOSE] : ""
                }
              >
                <MenuItem value="">
                  <em>Please Choose</em>
                </MenuItem>
                <MenuItem value="CUSTOMER_TEST">Customer Test</MenuItem>
                <MenuItem value="RD_SUPPORT">R&D Support</MenuItem>
                <MenuItem value="MARKETING_SUPPORT">Marketing Support</MenuItem>
                <MenuItem value="CLAIM_HANDLING">Claim Handling</MenuItem>
              </CustomDropdown>
            </Grid>
            {permissions?.viewTestRequestPriority && (
              <Grid item lg={3} md={3} sm={6} xs={6}>
                <InfoBox text={InfoMessages.PRIORITY}>
                  <CustomDropdown
                    disabled={!permissions?.editDescription}
                    error={!!(errors[Fields.PRIORITY] && touched[Fields.PRIORITY])}
                    id={Fields.PRIORITY}
                    name={Fields.PRIORITY}
                    onChange={handleLocalChange}
                    labelText={Labels.PRIORITY}
                    value={values[Fields.PRIORITY]}
                  >
                    <MenuItem value="NONE" disabled={values[Fields.PRIORITY] !== "NONE"}>
                      <em>NONE</em>
                    </MenuItem>
                    <MenuItem value="LOW">LOW</MenuItem>
                    <MenuItem value="MEDIUM">MEDIUM</MenuItem>
                    <MenuItem value="HIGH">HIGH</MenuItem>
                  </CustomDropdown>
                </InfoBox>
              </Grid>
            )}
          </Grid>
        </Box>
        <Box>
          <Box height={16} />
          <InfoBox compress text={InfoMessages.CUSTOMER_VALUE_CHAIN}>
            <h3>Customer & Value Chain Information</h3>
          </InfoBox>
          <Grid container justify="flex-start" direction="row" spacing={4}>
            <Grid item lg={6} md={6} sm={12} xs={12}>
              <CustomerSearchAutocomplete
                disabled={!permissions?.editDescription}
                error={!!(errors[Fields.DIRECT_CUSTOMER] && touched[Fields.DIRECT_CUSTOMER])}
                helperText={!values[Fields.DIRECT_CUSTOMER_LOCATION] ? "Select a customer" : ""}
                inputLabel={Labels.DIRECT_CUSTOMER}
                mandatory
                optionFetch={(query: string) => {
                  const countryId = values[Fields.DIRECT_CUSTOMER_LOCATION]?.id || 0;
                  return backgroundInformationApi.searchCustomers(countryId, query).then(({ data }) => data);
                }}
                onChange={(e: React.ChangeEvent<unknown>, customer: CustomerResponse | null) => {
                  if (customer) {
                    // Set all dependent fields as in customer object
                    setFieldValue(Fields.DIRECT_CUSTOMER, customer);
                    setFieldValue(Fields.DEBITOR_NO, customer.crmAccountNumber);
                    setFieldValue(Fields.DIRECT_CUSTOMER_LOCATION, customer.country);
                    setFieldValue(Fields.DIRECT_CUSTOMER_CGR, customer.crmBusinessHierarchyName);
                  } else {
                    // Reset all dependent fields
                    setFieldValue(Fields.DEBITOR_NO, "");
                    setFieldValue(Fields.DIRECT_CUSTOMER_CGR, "");
                    setFieldValue(Fields.DIRECT_CUSTOMER, null);
                    setFieldValue(Fields.DIRECT_CUSTOMER_LOCATION, null);
                  }
                  handleLocalChange(e);
                }}
                map={(value): { key: string; label: string } => ({
                  key: `${value.id}`,
                  label: value.name as string,
                })}
                value={values[Fields.DIRECT_CUSTOMER] as CustomerResponse | null | undefined}
              />
            </Grid>
            <Grid item lg={3} md={3} sm={6} xs={6}>
              <CustomTextField
                fullWidth
                disabled
                error={!!(errors[Fields.DEBITOR_NO] && touched[Fields.DEBITOR_NO])}
                id={Fields.DEBITOR_NO}
                label={Labels.DEBITOR_NO}
                name={Fields.DEBITOR_NO}
                onBlur={handleBlur}
                onChange={(e) => {
                  const crmAccountNumber = e.target.value;
                  setFieldValue(Fields.DEBITOR_NO, crmAccountNumber);
                  backgroundInformationApi.searchCustomerByCrmAccountNumber(crmAccountNumber).then(({ data }) => {
                    if (data.country) {
                      setFieldValue(Fields.DIRECT_CUSTOMER_LOCATION, {
                        name: data.country.name,
                        id: data.country.id,
                      });
                    }
                    setFieldValue(Fields.DIRECT_CUSTOMER, {
                      name: data.name,
                      id: data.id,
                    });
                  });
                }}
                value={values[Fields.DEBITOR_NO]}
              />
            </Grid>
            <Grid item lg={3} md={3} sm={6} xs={6}>
              <CountrySearchAutocomplete
                disabled
                error={!!(errors[Fields.DIRECT_CUSTOMER_LOCATION] && touched[Fields.DIRECT_CUSTOMER_LOCATION])}
                inputLabel={Labels.DIRECT_CUSTOMER_LOCATION}
                optionFetch={(query) => searchLocations(query)}
                onChange={(e: React.ChangeEvent<unknown>, country: CountryResponse | null) => {
                  if (country) {
                    setFieldValue(Fields.DIRECT_CUSTOMER_LOCATION, country);
                  } else {
                    // Reset all dependent fields
                    setFieldValue(Fields.DEBITOR_NO, "");
                    setFieldValue(Fields.DIRECT_CUSTOMER, null);
                    setFieldValue(Fields.DIRECT_CUSTOMER_LOCATION, null);
                  }
                  handleLocalChange(e);
                }}
                map={(value): { key: string; label: string } => ({
                  key: `${value.id}`,
                  label: value.name as string,
                })}
                value={values[Fields.DIRECT_CUSTOMER_LOCATION] as CountryResponse | null | undefined}
              />
            </Grid>
            <Grid item lg={3} md={3} sm={6} xs={6}>
              <CustomTextField
                fullWidth
                disabled={!permissions?.editDescription}
                helperText="Customer Group"
                id={Fields.DIRECT_CUSTOMER_CGR}
                label={Labels.DIRECT_CUSTOMER_CGR}
                name={Fields.DIRECT_CUSTOMER_CGR}
                onBlur={handleBlur}
                onChange={(e) => {
                  handleLocalChange(e);
                  setFieldValue(Fields.DIRECT_CUSTOMER_CGR, e.target.value);
                }}
                value={values[Fields.DIRECT_CUSTOMER_CGR]}
              />
            </Grid>
            <Grid item lg={6} md={6} sm={12} xs={12}>
              <OemSearchAutocomplete
                disabled={!permissions?.editDescription}
                error={!!(errors[Fields.OEM] && touched.oem)}
                helperText={[errors.oem && touched.oem ? errors.oem : ""].join(" ")}
                inputLabel={Labels.OEM}
                optionFetch={(query: string) => searchOems(query)}
                onChange={(e, val): void => {
                  if (values[Fields.OEM]?.id !== val?.id) {
                    setFieldValue(Fields.OEM, val);
                    handleDirtyChange();
                  }
                }}
                map={(value): { key: string; label: string } => ({
                  key: `${value.id}`,
                  label: value.name as string,
                })}
                value={values[Fields.OEM]}
              />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <Box height={16} />
          <h3>Responsibles</h3>
          <Grid container justify="flex-start" direction="row" spacing={4}>
            <Grid item xs={6}>
              <CustomTextField
                disabled
                fullWidth
                label="Requester Name"
                value={testRequestObj?.requester?.userName || user?.userName || ""}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomTextField
                disabled
                fullWidth
                label="Requester Email"
                value={testRequestObj?.requestorEmail || user?.userEmail}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomDatePicker
                disabled
                label="Submission Date"
                value={submissionOrApprovalDate}
                onChange={() => {
                  /* always disabled */
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomTextField
                disabled
                fullWidth
                label="Submitted by"
                value={testRequestObj?.submitter?.userEmail || ""}
              />
            </Grid>
            {testRequestObj?.completionDate && (
              <>
                <Grid item xs={6}>
                  <CustomTextField disabled fullWidth label="Completion Date" value={testRequestObj?.completionDate} />
                </Grid>
                <Grid item xs={6}>
                  <CustomTextField disabled fullWidth label="Completed by" value={testRequestObj?.completedBy || ""} />
                </Grid>
              </>
            )}
            <Grid item xs={6}>
              <CustomTextField disabled fullWidth label="Assignee" value={values.assignee?.userName || ""} />
            </Grid>
            <Grid item xs={6}>
              <ApproversInput values={testRequestObj?.approvers || []} />
            </Grid>
            <Grid item xs={6}>
              <InternalContactInput
                {...formikProps}
                disabled={!permissions?.editDescription}
                handleDirtyChange={handleDirtyChange}
              />
            </Grid>
            <Grid item xs={6}>
              <SuggestedApproverInput
                disabled={!permissions?.editDescription || isStatusPreparationAndFollowing || !values[Fields.CSC_LAB]}
                error={!!errors[Fields.APPROVER]}
                helperText={[errors[Fields.APPROVER] || ""].join(" ")}
                onChange={handleApproverChange}
                label={Labels.APPROVER}
                laboratory={values[Fields.CSC_LAB]}
                value={values[Fields.APPROVER]}
              />
            </Grid>
          </Grid>
        </Box>
      </>
    </Form>
  );
};

export default DescriptionFormInternal;
